import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { ARCH_LAYERS, MOAT_NODES } from "@/lib/data";
import { EASE, Tag } from "@/components/primitives";

export function ArchStack() {
  const [active, setActive] = useState(1);
  const layer = ARCH_LAYERS[active];
  return (
    <div className="grid gap-10 lg:grid-cols-2" data-testid="architecture-diagram">
      <div className="flex flex-col" role="tablist" aria-label="Architecture layers">
        {ARCH_LAYERS.map((l, i) => (
          <div key={l.name}>
            <button
              role="tab"
              aria-selected={active === i}
              data-testid={`arch-layer-${i}`}
              onClick={() => setActive(i)}
              onMouseEnter={() => setActive(i)}
              className={`group w-full rounded-xl border px-5 py-4 text-left transition-all duration-300 ${
                active === i
                  ? "border-electric/50 bg-panel2 shadow-[0_0_30px_rgba(11,99,243,0.10)]"
                  : "border-line bg-panel/60 hover:border-electric/30"
              }`}
            >
              <span className="flex items-center justify-between gap-4">
                <span className={`font-display text-sm font-semibold sm:text-base ${active === i ? "text-mist" : "text-mist/80"}`}>
                  {l.name}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute">{l.items.join(" • ")}</span>
              </span>
            </button>
            {i < ARCH_LAYERS.length - 1 && (
              <div className="flex justify-center py-1" aria-hidden="true">
                <ArrowDown className={`h-3.5 w-3.5 transition-colors duration-300 ${active === i ? "text-electric" : "text-mute/50"}`} />
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="relative min-h-[220px] rounded-2xl border border-line bg-panel/70 p-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35, ease: EASE }}
          >
            <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-electric">Layer {String(active + 1).padStart(2, "0")}</p>
            <h3 className="mt-3 font-display text-2xl font-bold tracking-tight text-mist">{layer.name}</h3>
            <p className="mt-4 leading-relaxed text-mute">{layer.desc}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {layer.items.map((item) => (
                <Tag key={item} tone={active === 3 ? "dev" : "planned"}>{item}</Tag>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

export function Flywheel() {
  const size = 560;
  const c = size / 2;
  const r = 200;
  const ANGLES = [270, 342, 15, 55, 125, 183, 228];
  const nodes = MOAT_NODES.map((label, i) => {
    const a = (ANGLES[i] * Math.PI) / 180;
    return { label, x: c + r * Math.cos(a), y: c + r * Math.sin(a) };
  });
  return (
    <div className="relative mx-auto w-full max-w-[560px]" data-testid="moat-flywheel" aria-label="TimePe compounding moat flywheel">
      <svg viewBox={`0 0 ${size} ${size}`} className="w-full">
        <circle cx={c} cy={c} r={r} fill="none" stroke="rgba(11,99,243,0.35)" strokeWidth={1.4} strokeDasharray="6 10" />
        <circle cx={c} cy={c} r={r * 0.66} fill="none" stroke="rgba(11,21,38,0.08)" strokeDasharray="2 6" />
        <circle cx={c} cy={c} r={56} fill="#FFFFFF" stroke="rgba(0,133,107,0.5)" strokeWidth={1.4} />
        <circle cx={c} cy={c} r={56} fill="none" stroke="rgba(0,133,107,0.4)" className="pulse-ring" />
        <image href="/assets/timepe-mark.png" x={c - 34} y={c - 34} width={68} height={68} />
        {nodes.map((n, i) => {
          const w = Math.max(96, n.label.length * 7.2 + 24);
          return (
            <g key={i}>
              <rect x={n.x - w / 2} y={n.y - 17} width={w} height={34} rx={17} fill="#FFFFFF" stroke="rgba(11,21,38,0.15)" />
              <text x={n.x} y={n.y + 4} textAnchor="middle" fill="#0A1628" fontSize={10.5} fontFamily="JetBrains Mono, monospace" letterSpacing={0.5}>
                {n.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export function BharatGrid() {
  const zones = [
    { label: "RURAL", x: 60 },
    { label: "SEMI-URBAN", x: 260 },
    { label: "URBAN", x: 460 },
  ];
  return (
    <svg viewBox="0 0 640 380" className="w-full" role="img" aria-label="Bharat access network flowing from rural to semi-urban to urban into the TimePe platform" data-testid="bharat-grid-visual">
      {zones.map((z, zi) => (
        <g key={z.label}>
          <text x={z.x + 55} y={30} textAnchor="middle" fill="#4C5F7A" fontSize={10} fontFamily="JetBrains Mono, monospace" letterSpacing={2}>
            {z.label}
          </text>
          {[0, 1, 2].map((row) => {
            const y = 90 + row * 100;
            const nodeX = z.x + 55 + (row % 2 ? 14 : -6);
            return (
              <g key={row}>
                <circle cx={nodeX} cy={y} r={7} fill="#FFFFFF" stroke={zi === 0 ? "#D97A1F" : "#0B63F3"} strokeOpacity={0.7} strokeWidth={1.4} />
                <path d={`M ${nodeX + 10} ${y} C ${nodeX + 70} ${y}, ${z.x + 130} ${y - 14 + row * 12}, ${z.x + 168} ${y - 20 + row * 10}`} fill="none" stroke={zi === 0 ? "#D97A1F" : "#0B63F3"} strokeOpacity={0.4} strokeWidth={1.2} className="flow-dash" />
              </g>
            );
          })}
        </g>
      ))}
      <circle cx={596} cy={190} r={34} fill="#FFFFFF" stroke="#0B63F3" strokeOpacity={0.6} strokeWidth={1.4} />
      <circle cx={596} cy={190} r={34} fill="none" stroke="#0B63F3" strokeOpacity={0.5} className="pulse-ring" />
      <image href="/assets/timepe-mark.png" x={596 - 21} y={190 - 21} width={42} height={42} />
    </svg>
  );
}
